import Button from "@/components/Button";
import { InfoIcon, cross } from "@/designSystemIcons";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";

interface Action {
  label: string;
  onClick: () => void;
}

interface PositiveAction extends Action {
  variant: "positive";
}

interface NegativeAction extends Action {
  variant: "negative";
}

interface LearnMoreAction extends Action {
  variant: "learn-more";
}

interface BaseModalProps {
  title: string;
  titleIcon?: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  description?: string;
  type?: "info" | "action";
  actions?: [NegativeAction, PositiveAction, LearnMoreAction?];
  className?: string;
  showCloseButton?: boolean;
}

export const BaseModal = ({
  children,
  title,
  titleIcon,
  description,
  onClose,
  type,
  actions,
  className,
  showCloseButton,
}: BaseModalProps) => {
  const { t } = useTranslation();
  // Lock scroll on all devices
  useScrollLock(true);

  const ref = useOutsideClick(() => {
    onClose();
  });
  return (
    <article
      className={cn(
        "p-2 mobile:p-4 bg-panel-middleground border-3 border-panel-foreground shadow-soft-dark-bottom mobile:min-w-90 w-90 mobile:w-auto max-w-102 flex flex-col rounded-lg relative",
        className,
      )}
      ref={ref}
    >
      {showCloseButton && (
        <>
          <button
            onClick={onClose}
            className="p-3 -m-3 stroke-dark-tertiary self-end z-1 absolute top-4 right-4"
          >
            <div className="w-4 h-4">{cross}</div>
          </button>
          {title && <div className="h-4 shrink-0"></div>}
        </>
      )}
      {title && (
        <p className="text-lg mobile:text-xl font-semibold text-center flex flex-row items-center justify-center gap-x-2">
          {title}
          {titleIcon}
        </p>
      )}
      {description && (
        <div
          className={cn(
            "flex flex-row justify-between items-center mt-4 py-1  gap-x-2",
            type === "info" && "bg-new-gray-700 rounded-lg px-2",
          )}
        >
          <p className="text-sm text-dark-secondary flex-1">{description}</p>
          {type === "info" && (
            <InfoIcon
              innerFill="white"
              outerFill="var(--color-primary-blue)"
              width="16"
              height="16"
            />
          )}
        </div>
      )}
      {children}
      {type === "action" && actions?.length > 0 && (
        <div className="flex flex-row justify-end items-center mt-4 gap-x-2">
          {actions[2]?.variant === "learn-more" && actions[2] && (
            <button
              className="text-xs text-dark-tertiary underline mt-auto mr-auto ml-2"
              onClick={actions[2].onClick}
            >
              {t("learnMore")}
            </button>
          )}
          {actions[0] && (
            <Button
              onClick={actions[0].onClick}
              label={actions[0].label}
              variant={"tertiary"}
            />
          )}
          {actions[1] && (
            <Button
              onClick={actions[1].onClick}
              label={actions[1].label}
              variant={"primary"}
            />
          )}
        </div>
      )}
    </article>
  );
};
